<template>
  <div class="paypal-card">
    <div
      id="paypal-button-container"
      class="paypal-button-container" />
    <div
      id="card-form"
      class="card_container">
      <div
        id="card-number-field-container"
        class="number" />
      <div
        id="card-expiry-field-container"
        class="date" />
      <div
        id="card-cvv-field-container"
        class="cvv" />
      <div
        id="card-name-field-container"
        class="name" />
    </div>
    <div
      v-if="backLink"
      class="button button-main paypal-card__button"
      @click="redirectToOrders">
      Back
    </div>
    <div
      v-else
      id="card-field-submit-button"
      class="button button-main paypal-card__button"
      @click="submit">
      Buy for {{ currencyValue(data.data.component.price.newPrice) }}
    </div>
  </div>
</template>

<script>
// import UiInput from 'Components/ui/Input/UiInput.vue';
import { loadScript } from '@paypal/paypal-js';
import AppModel from 'Models/AppModel';
import PaymentSystemModel from 'Models/PaymentSystemModel';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import { POPUP_PROMOCODE_APPLIED } from 'Models/PopupModel';
import { urlRegexp } from 'Root/helpers/regexps';
import { mapGetters, mapState } from 'vuex';
import { addBuyLogs } from 'Root/helpers/tools';

export default {
  name: 'PaypalCardPayment',

  components: {
    // UiInput,
  },

  props: {
    data: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      backLink: '',
      cardField: null,
      inputStyle: {
        body: {
          padding: '0',
        },
        input: {
          background: '#475C7F',
          color: '#A8BBDB',
          height: '40px',
          borderRadius: '3px',
          padding: '8px 13px',
          border: 'none',
          fontSize: '13px',
        },
        'input::placeholder': {
          color: '#A8BBDB',
          fontWeight: 500,
          transition: 'color .2s cubic-bezier(0, 0, 0.45, 1)',
        },
        'input:focus': {
          border: 'none',
          boxShadow: 'none',
          background: '#354a6c',
        },
        'input:focus.invalid': {
          boxShadow: 'none',
          borderColor: '#ff5e29',
          border: '1px solid #d9360b',
        },

        focusPlaceholder: {},
        '.invalid': { color: '#ff5e29' },
      },
    };
  },

  computed: {
    ...mapState(['activeGame', 'user']),
    ...mapGetters(['currencyValue']),
  },

  async beforeMount() {
    const that = this;
    const orderData = that.data.data.component.createOrderData(PaymentSystemModel.PAYPAL_CARD.id,
      that.data.eWallet,
      that.data.data.component.userEmail);
    const url = `${AppModel.API_URI}/product/${that.data.data.component.product.id}/buy`;
    let newOrder;
    try {
      newOrder = await that.$axios.post(url, orderData);
      await this.$router.replace({ query: { order: newOrder.data.orderId } });
    } catch (error) {
      addBuyLogs(error);
      return;
    }

    loadScript({ clientId: process.env.PAYPAL_APP_ID, components: ['card-fields'] }).then((paypal) => {
      that.cardField = paypal.CardFields({
        async createOrder() {
          if (newOrder.data.url) {
            return newOrder.data.url;
          }

          const errorDetail = orderData?.details?.[0];
          const errorMessage = errorDetail
            ? `${errorDetail.issue} ${errorDetail.description} (${orderData.debug_id})`
            : JSON.stringify(orderData);

          throw new Error(errorMessage);
        },

        async onApprove(data) {
          try {
            const response = await that.$axios.post(`${AppModel.INTERNAL_URL}/deposit/paypal`, {
              id: `${data.orderID}`,
              event_type: 'FRONTEND.CAPTURE.ORDER',
            });

            that.backLink = response.data.url;

            if (response.status === 200) {
              that.$vuedals.close();
              that.redirectToOrders();
            }
          } catch (e) {
            that.backLink = e.response.data.url;
            console.error(e);
          }
        },
      });

      if (that.cardField.isEligible()) {
        const nameField = that.cardField.NameField({
          style: that.inputStyle,
        });
        nameField?.render('#card-name-field-container');

        const numberField = that.cardField.NumberField({
          style: that.inputStyle,
        });
        numberField?.render('#card-number-field-container');

        const cvvField = that.cardField.CVVField({
          style: that.inputStyle,
        });
        cvvField?.render('#card-cvv-field-container');

        const expiryField = that.cardField.ExpiryField({
          style: that.inputStyle,
        });
        expiryField?.render('#card-expiry-field-container');
      }
    }).catch((e) => {
      console.error(e);
    });
  },

  methods: {
    redirectToOrders() {
      this.$cookie.delete(POPUP_PROMOCODE_APPLIED.cookieName);
      googleTagAnalytics.emitBuyItem(true);

      if (!this.backLink) {
        return;
      }

      if ('localStorage' in window) {
        localStorage.setItem('activeGameId', this.$store.state.activeGame.id);
      }

      if (this.backLink.match(urlRegexp)) {
        const pathToRedirect = new URL(this.backLink);
        const emailParam = pathToRedirect.searchParams.get('email');
        if (!this.user && !emailParam) {
          pathToRedirect.searchParams.append('email', this.data.data.component.userEmail);
        }

        document.location.href = pathToRedirect.href;
      }
    },

    submit() {
      this.cardField.submit();
    },
  },
};
</script>

<style lang="scss">
.paypal-popup {
  width: 340px !important;

  .mobile-panel-popup__body{
    padding: 0 30px 52px;

    .paypal-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;

      .paypal-button-container, .card_container, &__billing {
        width: 100%;
      }

      .card_container {
        display: grid;
        grid-gap: 16px;
        align-items: center;
        grid-template-areas: "number number"
                         "date cvv"
                         "name name";

        .name {
          grid-area: name;
        }

        .number {
          grid-area: number;
        }

        .date {
          grid-area: date;
        }

        .cvv {
          grid-area: cvv;
        }

        &>div {
          height: 40px;
        }
      }

      &__billing {
        display: flex;
        flex-direction: column;
        gap:4px;
      }

      .field {
        --height: 40px !important;
        --font-size: 13px !important;
        --color: #{$color--basic-light-gray} !important;
        --background-color: #{$color--basic-dark-gray} !important;
        --padding: 8px 13px !important;
      }

      &__button {
        margin-top: 1rem;
        font-size: 1.25rem;
        height: 48px;
        width: max-content;
        justify-self: center;
        padding: 0 16px;
        font-weight: 500;
        text-transform: unset;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>
